/* eslint-disable no-console */
'use client';

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

// Safe PostHog helper - avoids direct import that may cause issues
const safePostHog = {
  capture: (eventName, properties) => {
    try {
      if (typeof window !== 'undefined' && window.posthog) {
        window.posthog.capture(eventName, properties);
      }
    } catch (error) {
      console.warn('PostHog capture error:', error);
    }
  },
};

/**
 * BuilderABTestContext
 *
 * Provides A/B test tracking context to child components.
 * Enables conversion tracking across the offering page.
 */
const BuilderABTestContext = createContext(null);

/**
 * Hook to access A/B test context
 *
 * @returns {object|null} A/B test context with tracking methods
 *
 * @example
 * const { trackConversion, variationName } = useBuilderABTest();
 * trackConversion('invest_clicked', { amount: 1000 });
 */
export const useBuilderABTest = () => {
  const context = useContext(BuilderABTestContext);
  return context;
};

/**
 * Track a conversion event in both Builder.io and PostHog
 *
 * @param {string} contentId - Builder content ID
 * @param {string} variationId - A/B test variation ID
 * @param {string} event - Conversion event name
 * @param {object} metadata - Additional event metadata
 */
export const trackBuilderConversion = (contentId, variationId, event, metadata = {}) => {
  // Single PostHog event (Builder.io native tracking disabled for cost savings)
  safePostHog.capture('builder_conversion', {
    content_id: contentId,
    variation_id: variationId,
    conversion_goal: event,
    ...metadata,
  });
};

/**
 * BuilderABTestProvider
 *
 * Wraps components to provide A/B test context for conversion tracking.
 */
export const BuilderABTestProvider = ({
  children,
  contentId,
  variationId,
  variationName,
  offeringSlug,
}) => {
  /**
   * Track a conversion event
   *
   * @param {string} event - Event name (e.g., 'invest_clicked', 'invest_completed')
   * @param {object} metadata - Additional event data
   */
  const trackConversion = useCallback(
    (event, metadata = {}) => {
      trackBuilderConversion(contentId, variationId, event, {
        offering_slug: offeringSlug,
        variation_name: variationName,
        ...metadata,
      });
    },
    [contentId, variationId, variationName, offeringSlug],
  );

  /**
   * Track when user clicks the Invest button
   *
   * @param {object} options - Additional tracking options
   */
  const trackInvestClick = useCallback(
    (options = {}) => {
      trackConversion('invest_clicked', options);
    },
    [trackConversion],
  );

  /**
   * Track when user starts the investment flow
   *
   * @param {number} amount - Investment amount
   * @param {object} options - Additional tracking options
   */
  const trackInvestStarted = useCallback(
    (amount, options = {}) => {
      trackConversion('invest_started', { amount, ...options });
    },
    [trackConversion],
  );

  /**
   * Track when user completes an investment
   *
   * @param {number} amount - Investment amount
   * @param {object} options - Additional tracking options
   */
  const trackInvestCompleted = useCallback(
    (amount, options = {}) => {
      trackConversion('invest_completed', { amount, ...options });
    },
    [trackConversion],
  );

  /**
   * Track when user follows the offering
   */
  const trackFollow = useCallback(() => {
    trackConversion('follow_clicked');
  }, [trackConversion]);

  /**
   * Track generic CTA clicks
   *
   * @param {string} ctaName - Name of the CTA clicked
   * @param {object} options - Additional tracking options
   */
  const trackCTAClick = useCallback(
    (ctaName, options = {}) => {
      trackConversion('cta_clicked', { cta_name: ctaName, ...options });
    },
    [trackConversion],
  );

  const value = useMemo(
    () => ({
      // A/B test identifiers
      contentId,
      variationId,
      variationName,
      offeringSlug,

      // Whether we're in an A/B test
      isInTest: !!variationId,

      // Tracking methods
      trackConversion,
      trackInvestClick,
      trackInvestStarted,
      trackInvestCompleted,
      trackFollow,
      trackCTAClick,
    }),
    [
      contentId,
      variationId,
      variationName,
      offeringSlug,
      trackConversion,
      trackInvestClick,
      trackInvestStarted,
      trackInvestCompleted,
      trackFollow,
      trackCTAClick,
    ],
  );

  return <BuilderABTestContext.Provider value={value}>{children}</BuilderABTestContext.Provider>;
};

BuilderABTestProvider.propTypes = {
  children: PropTypes.node.isRequired,
  contentId: PropTypes.string,
  variationId: PropTypes.string,
  variationName: PropTypes.string,
  offeringSlug: PropTypes.string,
};

export default BuilderABTestContext;
